import { useState } from "react";
import { Button, Dropdown, Label, Popover, Tooltip } from "@heroui/react";
import { ChevronDown, Dices, MousePointerClick, Pause, Play, SkipForward, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ActionBarShell, BUY_CLASS, SELL_CLASS } from "../ActionBar";
import { PriceField } from "../../daytrader/RiskGuard";
import { parseNum } from "../../daytrader/dayTrader";
import { formatR } from "../chartSignals";
import {
  defaultPlan, planProblem, REPLAY_SPEEDS, type PracticeRecord, type PracticeTrade, type ReplaySpeed, type Scorecard,
} from "./replay";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mon 5 Oct 2026 · 13:00 UTC" */
export function replayStamp(sec: number): string {
  const d = new Date(sec * 1000);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${DAY_NAMES[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()} · ${hh}:${mm} UTC`;
}

const isoDay = (sec: number) => new Date(sec * 1000).toISOString().slice(0, 10);

const rTone = (r: number) => (r > 0.05 ? "text-profit" : r < -0.05 ? "text-loss" : "text-foreground");

/** Buy / Sell with a stop and target to set first: where am I wrong, where do I take profit? */
function PlaceTrade({
  side, entry, atrValue, riskUsd, priceFmt, onPlace,
}: {
  side: "LONG" | "SHORT";
  entry: number;
  atrValue: number | null;
  riskUsd: number;
  priceFmt: (p: number) => string;
  onPlace: (stop: number, target: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [stop, setStop] = useState("");
  const [target, setTarget] = useState("");
  const buy = side === "LONG";
  const openWithDefaults = (next: boolean) => {
    if (next) {
      const plan = defaultPlan(side, entry, atrValue);
      setStop(priceFmt(plan.stop));
      setTarget(priceFmt(plan.target));
    }
    setOpen(next);
  };
  const s = parseNum(stop);
  const t = parseNum(target);
  const problem = s === null || t === null ? "Enter a stop and a target." : planProblem(side, entry, s, t);
  const risk = s === null ? 0 : Math.abs(entry - s);
  const rr = s !== null && t !== null && risk > 0 ? Math.abs(t - entry) / risk : null;
  const pct = (p: number | null) => (p === null ? "" : `${(((p - entry) / entry) * 100).toFixed(2)}%`);

  return (
    <Popover isOpen={open} onOpenChange={openWithDefaults}>
      <Button size="md" className={buy ? BUY_CLASS : SELL_CLASS}>
        {buy ? "Buy" : "Sell"}
      </Button>
      <Popover.Content placement="top" className="w-72">
        <Popover.Dialog className="space-y-3 p-3">
          <div>
            <p className="text-sm font-semibold">{buy ? "Practice buy" : "Practice sell"} at <span className="num">{priceFmt(entry)}</span></p>
            <p className="text-xs text-muted">The last candle's close. Set where you're wrong (stop) and where you take profit (target). Not saved to your trades.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <PriceField label={`Stop ${pct(s)}`} value={stop} onChange={setStop} hint="Where the idea is wrong" />
            <PriceField label={`Target ${pct(t)}`} value={target} onChange={setTarget} hint="Where you take profit" />
          </div>
          {problem ? (
            <p className="text-xs text-warning">{problem}</p>
          ) : (
            <p className="text-xs text-muted">
              Reward <span className="num font-semibold text-foreground">{rr!.toFixed(2)}×</span> the risk
              {riskUsd > 0 && risk > 0 && (
                <> · risking <span className="num text-foreground">${riskUsd.toFixed(2)}</span> buys <span className="num text-foreground">{(riskUsd / risk).toPrecision(3)}</span> coins</>
              )}
              {rr! < 1.5 && <span className="text-warning"> · the engine skips trades under 1.5×</span>}
            </p>
          )}
          <Button
            fullWidth
            size="sm"
            isDisabled={problem !== null}
            className={cn("font-semibold", buy ? "bg-profit text-on-profit" : "bg-loss text-on-loss")}
            onPress={() => {
              onPlace(s!, t!);
              setOpen(false);
            }}
          >
            {buy ? "Place practice buy" : "Place practice sell"}
          </Button>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

export interface ReplayBarProps {
  mode: "picking" | "active";
  /** Replay time: the close of the last visible candle. */
  nowSec: number | null;
  loading: boolean;
  error: string | null;
  playing: boolean;
  speed: ReplaySpeed;
  /** No more loaded candles to reveal. */
  atEnd: boolean;
  /** …because replay time has reached the present. */
  reachedToday: boolean;
  openTrade: PracticeTrade | null;
  openR: number | null;
  card: Scorecard;
  record: PracticeRecord;
  lastClose: number | null;
  atrValue: number | null;
  riskUsd: number;
  priceFmt: (p: number) => string;
  onRandom: () => void;
  onPickDate: (sec: number) => void;
  onCancel: () => void;
  onTogglePlay: () => void;
  onStep: () => void;
  onSpeed: (s: ReplaySpeed) => void;
  onPlace: (side: "LONG" | "SHORT", stop: number, target: number) => void;
  onCloseTrade: () => void;
  onContinue: () => void;
  onExit: () => void;
}

/**
 * The replay's controls, under the chart: choose a start (a candle, a date,
 * or a random day), then step / play, practise trades and keep score.
 */
export function ReplayBar(p: ReplayBarProps) {
  const todaySec = Math.floor(Date.now() / 1000);
  const [day, setDay] = useState(() => isoDay(todaySec - 30 * 86400));

  if (p.mode === "picking") {
    return (
      <ActionBarShell
        left={
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Choose where the replay starts">
            <span className="font-semibold text-foreground">Replay from</span>
            <span className="flex items-center gap-1 text-muted">
              <MousePointerClick className="size-3.5" /> a candle you click
            </span>
            <span className="text-muted">·</span>
            <Button size="sm" variant="secondary" onPress={p.onRandom}>
              <Dices className="size-3.5" /> Random day
            </Button>
            <span className="text-muted">·</span>
            <span className="flex items-center gap-1">
              <input
                type="date"
                aria-label="Replay start date"
                value={day}
                max={isoDay(todaySec - 86400)}
                min="2020-01-01"
                onChange={e => setDay(e.target.value)}
                className="num h-8 rounded-lg border border-border bg-surface-secondary px-2 text-xs text-foreground outline-none focus-visible:ring-2 focus-visible:ring-focus"
              />
              <Button size="sm" variant="secondary" isDisabled={!day} onPress={() => p.onPickDate(Date.parse(`${day}T00:00:00Z`) / 1000)}>
                Go
              </Button>
            </span>
            <Button size="sm" variant="ghost" className="text-muted" onPress={p.onCancel}>Cancel</Button>
          </div>
        }
      />
    );
  }

  const { card } = p;
  const winRate = p.record.trades > 0 ? Math.round((p.record.won / p.record.trades) * 100) : null;
  const canTrade = p.lastClose !== null && !p.atEnd;
  return (
    <ActionBarShell
      left={
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2" role="group" aria-label="Replay controls">
          <span className="flex items-center gap-2">
            <span className="rounded bg-warning/15 px-1.5 py-0.5 text-3xs font-bold tracking-wide text-warning">REPLAY</span>
            <span className="num text-foreground">{p.nowSec !== null ? replayStamp(p.nowSec) : "…"}</span>
          </span>

          <span className="flex items-center gap-0.5">
            <Tooltip delay={300}>
              <Button
                isIconOnly
                size="sm"
                variant="ghost"
                aria-label={p.playing ? "Pause" : "Play"}
                isDisabled={p.loading || (p.atEnd && !p.playing)}
                onPress={p.onTogglePlay}
                className="text-foreground"
              >
                {p.playing ? <Pause className="size-4" /> : <Play className="size-4" />}
              </Button>
              <Tooltip.Content>{p.playing ? "Pause" : "Play: one candle after another"}</Tooltip.Content>
            </Tooltip>
            <Tooltip delay={300}>
              <Button isIconOnly size="sm" variant="ghost" aria-label="Next candle" isDisabled={p.loading || p.atEnd || p.playing} onPress={p.onStep} className="text-foreground">
                <SkipForward className="size-4" />
              </Button>
              <Tooltip.Content>Next candle</Tooltip.Content>
            </Tooltip>
            <Dropdown>
              <Button size="sm" variant="ghost" aria-label={`Speed ${p.speed}×`} className="num gap-0.5 px-2 text-foreground">
                {p.speed}× <ChevronDown className="size-3 text-muted" />
              </Button>
              <Dropdown.Popover placement="top">
                <Dropdown.Menu aria-label="Replay speed" onAction={k => p.onSpeed(Number(k) as ReplaySpeed)}>
                  {REPLAY_SPEEDS.map(s => (
                    <Dropdown.Item key={s} id={String(s)} textValue={`${s}×`}>
                      <Label className={cn("num", s === p.speed && "font-semibold")}>{s}× · {s} candle{s === 1 ? "" : "s"} a second</Label>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          </span>

          <Tooltip delay={300}>
            <Tooltip.Trigger aria-label="Your practice score" className="flex items-center gap-1.5">
              {card.closed === 0 ? (
                <span className="text-muted">No practice trades yet</span>
              ) : (
                <>
                  <span className="num text-profit">{card.won}W</span>
                  <span className="num text-loss">{card.lost}L</span>
                  <span className={cn("num font-semibold", rTone(card.totalR))}>{formatR(card.totalR)}</span>
                </>
              )}
            </Tooltip.Trigger>
            <Tooltip.Content className="max-w-64 text-xs">
              This replay: {card.closed} closed{card.open > 0 ? `, ${card.open} open` : ""}.{" "}
              {p.record.trades > 0
                ? `All your replay practice in this browser: ${p.record.trades} trades, ${winRate}% won, ${formatR(p.record.totalR)}.`
                : "Your practice record builds up here as you close trades."}{" "}
              R = result in units of your risk (1R = what you'd lose at the stop). Before fees.
            </Tooltip.Content>
          </Tooltip>

          {p.loading && <span className="text-muted">Loading candles…</span>}
          {p.error && <span className="text-danger">{p.error}</span>}
          {p.atEnd && !p.loading && (
            p.reachedToday
              ? <span className="text-muted">You've reached today.</span>
              : <Button size="sm" variant="secondary" onPress={p.onContinue}>Load the next candles</Button>
          )}

          <Tooltip delay={300}>
            <Button isIconOnly size="sm" variant="ghost" aria-label="Exit replay" onPress={p.onExit} className="text-muted hover:text-foreground">
              <X className="size-4" />
            </Button>
            <Tooltip.Content>Exit replay: back to live</Tooltip.Content>
          </Tooltip>
        </div>
      }
      right={
        p.openTrade ? (
          <div className="col-span-2 flex h-10 items-center justify-between gap-2 rounded-lg bg-default px-3">
            <span className="flex items-center gap-1.5">
              <span className={cn("font-semibold", p.openTrade.side === "LONG" ? "text-profit" : "text-loss")}>
                YOU {p.openTrade.side === "LONG" ? "BUY" : "SELL"}
              </span>
              {p.openR !== null && <span className={cn("num font-semibold", rTone(p.openR))}>{formatR(p.openR)}</span>}
            </span>
            <Button size="sm" variant="ghost" onPress={p.onCloseTrade}>Close now</Button>
          </div>
        ) : canTrade ? (
          <>
            <PlaceTrade side="LONG" entry={p.lastClose!} atrValue={p.atrValue} riskUsd={p.riskUsd} priceFmt={p.priceFmt} onPlace={(s, t) => p.onPlace("LONG", s, t)} />
            <PlaceTrade side="SHORT" entry={p.lastClose!} atrValue={p.atrValue} riskUsd={p.riskUsd} priceFmt={p.priceFmt} onPlace={(s, t) => p.onPlace("SHORT", s, t)} />
          </>
        ) : undefined
      }
    />
  );
}
